import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircleFilled, DisconnectOutlined, LinkOutlined } from '@ant-design/icons'

import type { ConnectionState } from '../../../services/connection-monitor.service'
import css from './ui.module.scss'

type Notice = {kind: 'lost'; target: 'server' | 'database'} | {kind: 'restored'} | null
const labels = {checking: 'Проверяем…', online: 'Онлайн', offline: 'Нет связи', unknown: 'Не проверена'}

export const ConnectionStatus = ({state, className = ''}: {state: ConnectionState; className?: string}) => {
    const [notice, setNotice] = useState<Notice>(null)
    const hadOutage = useRef(false)
    const outageTarget = state.server === 'offline' ? 'server' : state.database === 'offline' ? 'database' : null
    const healthy = state.server === 'online' && state.database === 'online'
    const status = outageTarget ? 'offline' : healthy ? 'online' : 'checking'
    const details = `Сервер: ${labels[state.server]}\nБаза данных: ${labels[state.database]}`

    useEffect(() => {
        if (outageTarget) {
            hadOutage.current = true
            setNotice({kind: 'lost', target: outageTarget})
        } else if (healthy && hadOutage.current) {
            hadOutage.current = false
            setNotice({kind: 'restored'})
        }
    }, [outageTarget, healthy])

    useEffect(() => {
        if (notice?.kind !== 'restored') return
        const timeout = setTimeout(() => setNotice(null), 3000)
        return () => clearTimeout(timeout)
    }, [notice])

    const restored = notice?.kind === 'restored'
    const databaseLost = notice?.kind === 'lost' && notice.target === 'database'

    return <>
        <footer className={`${css.desktop} ${className}`} aria-label='Состояние соединения'>
            <div className={css.service} data-state={status} title={details} role='status' aria-live='polite' aria-atomic='true'>
                <LinkOutlined aria-hidden='true' /><span>Соединение</span><i aria-hidden='true' /><strong>{labels[status]}</strong>
            </div>
        </footer>
        {notice && createPortal(<div className={`${css.notice}${restored ? ` ${css.restored}` : ''}`} role='status' aria-live='polite' aria-atomic='true'>
            <span className={css.notice_icon} aria-hidden='true'>{restored ? <CheckCircleFilled /> : <DisconnectOutlined />}</span>
            <div><strong>{restored ? 'Соединение восстановлено' : databaseLost ? 'База данных недоступна' : 'Нет связи с сервером'}</strong>
                <p>{restored ? 'Можно продолжать работу' : databaseLost ? 'Сервер доступен. Ожидаем восстановления базы данных.' : 'Проверяем подключение автоматически…'}</p>
            </div>
        </div>, document.body)}
    </>
}
